import type {
  MarketingRouterCheckResult,
  MarketingRouterWarning,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

import type { MarketingRouterRule } from '~/types/marketingRouterRule';

import { MATCH_FIELD_LABELS, toMatchValues } from './marketingRouterMatch';

export type MarketingRouterOverlapWarning = Extract<MarketingRouterWarning, { type: 'overlapping-rule' }>;

/** The cross-router overlap findings of a check, in the order svc-promo reports them. */
export function getOverlapWarnings(check: MarketingRouterCheckResult): Array<MarketingRouterOverlapWarning> {
  return check.warnings.filter(
    (warning): warning is MarketingRouterOverlapWarning => warning.type === 'overlapping-rule',
  );
}

/**
 * Identity of one overlap, stable while the operator edits: an acknowledgement
 * is of "this rule overlaps that rule", not of a particular message text.
 */
export function getOverlapConflictKey(warning: MarketingRouterOverlapWarning): string {
  return `${warning.conflictingMarketingRouterId}:${warning.conflictingRuleId}`;
}

export function getConflictingRuleEditPath(warning: MarketingRouterOverlapWarning): string {
  return `/marketing/marketing-router-rules/${encodeURIComponent(warning.conflictingMarketingRouterId)}/edit`;
}

type Match = MarketingRouterRule['match'];

/**
 * What the overlap check depends on, as a comparable string: two rules with
 * the same snapshot get the same findings.
 */
export function getMatchSnapshot(rule: Pick<MarketingRouterRule, 'match' | 'start_date' | 'end_date'>): string {
  return JSON.stringify({
    match: MATCH_FIELD_LABELS.map(([field]) => toMatchValues(rule.match[field])),
    start_date: rule.start_date,
    end_date: rule.end_date,
  });
}

export type OverlapGate = { canSave: true } | { canSave: false; reason: 'blocking-overlap' | 'confirmation-required' };

/**
 * Whether overlap findings allow the save.
 *
 * The check itself re-runs on every edit, so findings are always current.
 * Errors (equal specificity — router order would decide) block outright.
 * Warnings (specificity settles it) are non-blocking once the operator has
 * confirmed every one; an edit that introduces a new conflict re-arms the
 * gate, because acknowledgements are keyed by the other rule.
 */
export function getOverlapGate({
  warnings,
  acknowledgedKeys,
}: {
  warnings: Array<MarketingRouterOverlapWarning>;
  acknowledgedKeys: Array<string>;
}): OverlapGate {
  if (warnings.some((warning) => warning.level === 'error')) {
    return { canSave: false, reason: 'blocking-overlap' };
  }

  const confirmable = warnings.filter((warning) => warning.level === 'warning');
  if (confirmable.some((warning) => !acknowledgedKeys.includes(getOverlapConflictKey(warning)))) {
    return { canSave: false, reason: 'confirmation-required' };
  }
  return { canSave: true };
}

export function getOverlapGateMessage(gate: OverlapGate): string | null {
  if (gate.canSave === true) {
    return null;
  }
  switch (gate.reason) {
    case 'blocking-overlap':
      return 'Another rule can serve the same traffic with equal specificity. Resolve the conflict under Overlaps before saving.';
    case 'confirmation-required':
      return 'Confirm each overlap under Overlaps before saving.';
  }
}

export type RuleDefinitionRow = { label: string; value: string };

/**
 * The other rule's targeting, as table rows: every match field it constrains,
 * then its date window. Unset UTM fields are left out — they target
 * everything, and listing them as blanks reads as if they were configured.
 *
 * Regions is the exception and is always shown: an unset region reaches every
 * region, which is exactly how a rule ends up overlapping one that names a
 * region, so leaving the row out hides the reason for the overlap.
 *
 * The overlap warning names the other rule but does not carry its match, so
 * this reads the copy the form already holds.
 */
export function getRuleDefinitionRows(
  rule: Pick<MarketingRouterRule, 'match' | 'start_date' | 'end_date'> | undefined,
): Array<RuleDefinitionRow> {
  if (rule == null) {
    return [];
  }

  const rows = MATCH_FIELD_LABELS.map(([field, label]) => ({
    label,
    value: toMatchValues(rule.match[field]).join(', '),
  })).filter((row) => row.value !== '' || row.label === 'Regions');
  const regions = rows.find((row) => row.label === 'Regions');
  if (regions != null && regions.value === '') {
    regions.value = 'Anywhere';
  }

  if (rule.start_date || rule.end_date) {
    rows.push({ label: 'Dates', value: `${rule.start_date || '—'} to ${rule.end_date || '—'}` });
  }

  return rows;
}

/** The labels of the fields `other` constrains and `rule` leaves open. */
function getFieldsLeftOpen(rule: Match | undefined, other: Match | undefined): Array<string> {
  if (rule == null || other == null) {
    return [];
  }
  return MATCH_FIELD_LABELS.filter(
    ([field]) => toMatchValues(rule[field]).length === 0 && toMatchValues(other[field]).length > 0,
  ).map(([, label]) => label);
}

function toFieldList(labels: Array<string>): string {
  return labels.length > 1 ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}` : labels[0];
}

/**
 * How to break a tie, naming the fields that would do it.
 *
 * A tie means each rule constrains something the other leaves open — the
 * region rule catches every campaign, the campaign rule catches every region
 * — so the fix is to close one of those gaps rather than to guess.
 */
function getTieBreakingAdvice(
  otherName: string,
  thisRule: Match | undefined,
  otherRule: Match | undefined,
): string | null {
  const openHere = getFieldsLeftOpen(thisRule, otherRule);
  const openThere = getFieldsLeftOpen(otherRule, thisRule);
  if (openHere.length === 0 && openThere.length === 0) {
    return null;
  }

  const options = [
    openHere.length > 0 ? `set ${toFieldList(openHere)} on this rule` : null,
    openThere.length > 0 ? `add ${toFieldList(openThere)} to ${otherName}` : null,
  ].filter((option): option is string => option != null);

  return `Be specific about the UTMs: ${options.join(', or ')} — the rule that targets more narrowly then takes the visitors they share.`;
}

/**
 * The overlap explained as one bullet per idea: which rule wins and why, then
 * what that costs the other one.
 *
 * This mirrors the contract's `message` without its specificity scores — the
 * numbers explain the verdict to us, not to the operator, who acts on which
 * rule wins. Kept here while the wording settles; it belongs in the contract
 * next to the verdict once it does.
 */
export function getOverlapBullets(
  warning: MarketingRouterOverlapWarning,
  rules: { thisRule?: Pick<MarketingRouterRule, 'match'>; otherRule?: Pick<MarketingRouterRule, 'match'> } = {},
): Array<string> {
  const other = `“${warning.conflictingRuleName}”`;
  const narrowedBy = warning.narrowedBy.length > 0 ? ` (narrowed by ${warning.narrowedBy.join(', ')})` : '';

  if (warning.winnerRuleId == null) {
    const advice = getTieBreakingAdvice(other, rules.thisRule?.match, rules.otherRule?.match);
    return [
      warning.overlapKind === 'identical'
        ? `${other} matches exactly the same traffic as this rule, so neither is more specific.`
        : `${other} overlaps this rule and neither one is more specific.`,
      'Which experiment a visitor enters would depend on router order.',
      ...(advice == null ? [] : [advice]),
      advice == null
        ? 'Make one rule more specific, shift the dates, or disable one of them.'
        : 'Or shift the dates so the two never run together, or disable one of them.',
    ];
  }

  const thisRuleWins = warning.winnerRuleId === warning.ruleId;
  const winner = thisRuleWins ? 'this rule' : other;
  const loser = thisRuleWins ? other : 'this rule';
  const shape =
    warning.overlapKind === 'subset'
      ? thisRuleWins
        ? `This rule sits inside ${other}${narrowedBy}`
        : `${other} sits inside this rule${narrowedBy}`
      : thisRuleWins
        ? `This rule partly overlaps ${other}`
        : `${other} partly overlaps this rule`;

  return [
    `${shape} and is more specific.`,
    `Every visitor matching both enters ${winner} and ${loser} only receives the rest, so ${
      thisRuleWins ? other : 'this experiment'
    } will not measure the whole population its match describes.`,
  ];
}
